/**
 * Lead scoring algorithm
 * Calculates engagement scores (0-100) and determines lead quality (Hot/Warm/Cold)
 */

export interface LeadActivity {
  unitViews?: number;
  unitClicks?: number;
  unitDetailViews?: number;
  formSubmitted?: boolean;
  meetingScheduled?: boolean;
  unitPrice?: number | null;
}

export type LeadQuality = 'Hot' | 'Warm' | 'Cold';

/**
 * Calculate engagement score based on lead activity
 * Score range: 0-100
 */
export function calculateEngagementScore(activity: LeadActivity): number {
  let score = 0;

  // Base score from unit views (max +25 points)
  if (activity.unitViews) {
    const viewScore = Math.min(activity.unitViews * 5, 25); // 5 points per view, max 25
    score += viewScore;
  }

  // Multiple unit views bonus (engagement depth) - +10 points
  if (activity.unitViews && activity.unitViews > 1) {
    score += 10;
  }

  // Unit clicks from map (engagement indicator) - +5 points per click
  if (activity.unitClicks) {
    score += Math.min(activity.unitClicks * 5, 15); // Max 15 points from clicks
  }

  // Unit detail page view (deeper engagement) - +15 points
  if (activity.unitDetailViews && activity.unitDetailViews > 0) {
    score += 15;
  }

  // Form submission (high intent) - +20 points
  if (activity.formSubmitted) {
    score += 20;
  }

  // Meeting scheduled (very high intent) - +25 points
  if (activity.meetingScheduled) {
    score += 25;
  }

  // Unit price tier bonus (higher value = higher intent)
  if (activity.unitPrice) {
    score += calculateUnitPriceScore(activity.unitPrice);
  }

  // Cap at 100
  return Math.min(score, 100);
}

/**
 * Calculate score bonus based on unit price tier
 * Higher value units indicate higher purchasing power and intent
 */
export function calculateUnitPriceScore(price: number): number {
  // High-value units (> $900k) - +10 points
  if (price > 900000) {
    return 10;
  }
  // Medium-value units ($600k - $900k) - +5 points
  if (price >= 600000) {
    return 5;
  }
  // Lower-value units (< $600k) - +0 points (still valuable, but no bonus)
  return 0;
}

/**
 * Determine lead quality based on engagement score
 * Hot: 70-100 (high intent, ready to buy)
 * Warm: 40-69 (moderate interest, needs nurturing)
 * Cold: 0-39 (early stage, needs more engagement)
 */
export function determineLeadQuality(score: number): LeadQuality {
  if (score >= 70) {
    return 'Hot';
  } else if (score >= 40) {
    return 'Warm';
  } else {
    return 'Cold';
  }
}

/**
 * Calculate engagement score from unit interest record
 * Convenience function that extracts activity from database record
 */
export function calculateScoreFromRecord(record: {
  view_count: number;
  click_count: number;
  form_submitted: boolean;
  meeting_scheduled: boolean;
  unit?: { price: number | null } | null;
}): number {
  const activity: LeadActivity = {
    unitViews: record.view_count,
    unitClicks: record.click_count,
    unitDetailViews: record.view_count > 0 ? 1 : 0, // If they viewed, they saw detail page
    formSubmitted: record.form_submitted,
    meetingScheduled: record.meeting_scheduled,
    unitPrice: record.unit?.price || null,
  };

  return calculateEngagementScore(activity);
}

/**
 * Get lead quality from score
 * Convenience function
 */
export function getLeadQualityFromScore(score: number): LeadQuality {
  return determineLeadQuality(score);
}

